import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, Users, User, Bell, Shield } from 'lucide-react';
import Wordmark from './Wordmark';
import UnreadBadge from './UnreadBadge';
import { useUser } from '../../contexts/UserContext';
import { canSee } from '../../lib/adminNav';

const navItems = [
  { id: 'feed', path: '/feed', icon: Home, label: 'Home' },
  { id: 'explore', path: '/explore', icon: Search, label: 'Explore' },
  { id: 'community', path: '/community', icon: Users, label: 'Tables' },
  { id: 'profile', path: '/profile', icon: User, label: 'Profile' },
];

const TopNav = ({ unreadCount = 0 }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const alertsActive = pathname.startsWith('/notifications');
  // Same rule as AdminRoute: moderator and up. Staff don't have /admin.
  const { userData } = useUser();
  const showAdmin = canSee(userData?.app_role, 'moderator');
  const adminActive = pathname.startsWith('/admin');

  return (
    <div
      className="sticky top-0 z-30"
      style={{
        backgroundColor: 'var(--nav-bg)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--nav-border)',
        paddingTop: 'var(--sat)',
      }}
    >
      {/* Width must match AppShell's content cap — see the note there. */}
      <div className="max-w-[1088px] mx-auto px-6 flex items-center justify-between h-14">
        {/* Logo */}
        <button
          onClick={() => navigate('/feed')}
          aria-label="DSP Cafe home"
          className="flex items-center gap-2 transition-opacity hover:opacity-80"
        >
          {/* Wordmark only: the ringed mark read as a heavy sticker in the light
              bar (user, 2026-09-30). The mark lives where it has room. */}
          <Wordmark className="text-xl" style={{ color: 'var(--gradient-base)' }} />
        </button>

        {/* Nav Items */}
        <nav className="flex items-center gap-1">
          {navItems.map(({ id, path, icon: Icon, label }) => {
            const isActive = pathname.startsWith(path);
            return (
              <button
                key={id}
                onClick={() => navigate(path)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive ? '' : 'hover:brightness-110'
                }`}
                style={{
                  color: isActive ? 'var(--brand-primary)' : 'var(--text-tertiary)',
                  backgroundColor: isActive ? 'var(--pill-bg)' : 'transparent',
                }}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            );
          })}
          {/* Icon-only bell — a 5th labeled pill reads as clutter on desktop */}
          <button
            onClick={() => navigate('/notifications')}
            title="Notifications"
            className={`relative p-2 rounded-lg transition-all ${alertsActive ? '' : 'hover:brightness-110'}`}
            style={{
              color: alertsActive ? 'var(--brand-primary)' : 'var(--text-tertiary)',
              backgroundColor: alertsActive ? 'var(--pill-bg)' : 'transparent',
            }}
          >
            <Bell size={20} />
            <UnreadBadge count={unreadCount} />
          </button>
          {showAdmin && (
            <button
              onClick={() => navigate('/admin')}
              title="Admin"
              aria-label="Admin"
              className={`p-2 rounded-lg transition-all ${adminActive ? '' : 'hover:brightness-110'}`}
              style={{
                color: adminActive ? 'var(--brand-primary)' : 'var(--text-tertiary)',
                backgroundColor: adminActive ? 'var(--pill-bg)' : 'transparent',
              }}
            >
              <Shield size={20} />
            </button>
          )}
        </nav>
      </div>
    </div>
  );
};

export default TopNav;
